import Backbone from 'backbone'
import Importable from 'scoreboard/imports/model/importable'
import nestCollection from 'scoreboard/components/nest-collection'
import DatasetFields from './dataset-fields'
import _ from 'lodash'
import i18n from 'scoreboard/components/i18n'
import isNumericDataType from 'scoreboard/components/is-numeric-data-type'
import fieldAggregationDescription from 'scoreboard/datasets/field-aggregation-description'
import datasetStatus from '../not-available/dataset-status'
import datasetPaths from 'scoreboard/datasets/edit/dataset-paths'
import permissions from 'scoreboard/components/permissions'
import unwrapModel from 'scoreboard/components/unwrap-model'
import DataType from 'scoreboard/datasets/model/data-type'
const { NUMBER, DATASET_LINK, YES_NO } = DataType
import { getIntermediateTableColumnFromBasicEquation } from
		'scoreboard/datasets/equations/clean-field-equation-formatter'
import constants from 'scoreboard/components/constants'
import wrapModel from 'scoreboard/components/wrap-model'
const { UNSAVED_PREFIX, DATASET_FIELD_TYPE } = constants

/**
 * Value sent to the client when rollup values are not allowed because the
 * user doesn't have permission to see rollups (#20557)
 * NOTE: This is an "em dash" and not a hyphen. The difference is meaningful to
 * determine the correct ordering for sorting
 */
const ROLLUPS_NOT_VIEWABLE = '—'

/**
 * Returns whether the given fieldId indicates a Total number of records
 * instead of a specific field.
 */
function isTotal(fieldId) {
	return fieldId == Dataset.TOTAL
}

/**
 * @extends Importable
 */
const Dataset = Importable.extend(/** @lends Dataset.prototype */{

	section: 'datasets',
	domainType: 'dataset',

	urlRoot: 'api/datasets',

	getSourceDataPreviewUrl: function() {
		return 'api/datasets/spreadsheets/preview'
	},

	getUploadSpreadsheetUrl: function() {
		return 'api/datasets/spreadsheets/upload'
	},

	getImportSpreadsheetUrl: function() {
		return 'api/datasets/spreadsheets/import'
	},

	getUploadExternalFileUrl: function() {
		return 'api/datasets/spreadsheets/external'
	},

	getValidateDataSourceUrl: function() {
		return '/api/datasets/validate-data-source'
	},

	getValidateDbConnectionUrl: function() {
		return 'api/datasets/validate-connection'
	},

	getTransformationPreviewUrl: function() {
		return 'api/datasets/transformation-preview'
	},

	getValidatePrimaryKeyUrl: function() {
		return 'api/datasets/validate-primary-key'
	},

	getValidateFieldUrl: function() {
		return `api/datasets/${this.id}/validate-field`
	},

	getFieldErrorsUrl: function() {
		return `api/datasets/${this.id}/field-errors`
	},

	initialize: function(attributes, options) {
		Importable.prototype.initialize.apply(this, arguments)
		this.setup(options)
		this.on('sync', () => this.setup(options))
		this.on('change:visibleLinkedFields', () => this.updateVisibleLinkedFieldsById())

		datasetStatus().then(status => {
			this.listenTo(status, 'change', props => {
				this.set('status', props.status)
			})
		})
	},

	setup: function(options) {
		const {fields, filters, tables, permissions} = this.attributes

		/**
		 * @type {Backbone.Collection<DatasetField>}
		 */
		this.fields = new DatasetFields(fields)
		nestCollection({
			parent: this,
			parentAttribute: 'fields',
			children: this.fields
		})

		/**
		 * @typedef DatasetFilter
		 * @type {Backbone.Collection<DatasetFilter>}
		 */
		this.filters = new Backbone.Collection(filters)
		nestCollection({
			parent: this,
			parentAttribute: 'filters',
			children: this.filters
		})

		/**
		 * @type {Backbone.Collection<DatasetTable>}
		 */
		this.tables = new Backbone.Collection(tables)
		nestCollection({
			parent: this,
			parentAttribute: 'tables',
			children: this.tables
		})

		this.permissionsById = _.keyBy(permissions, 'id')
		this.combinedFields = _.get(options, 'combinedFields')
		this.combinedFieldsById = _.get(options, 'combinedFieldsById')
		if (_.isNil(this.combinedFieldsById)) {
			this.updateCombinedFields()
		}
		this.addEditFields()

		this.setupDatasetsById()

		this.setupBuiltInByPrincipalId()

		this.updateVisibleLinkedFieldsById()
	},

	/**
	 * Initializes the datasetsById property, which is a mapping of dataset ID to
	 * a Dataset model for all linked datasets.
	 * @param is2ndLevelLinkedDataset {boolean} true if this is a linked dataset
	 * of the base dataset, where we only want to populate the linked datasets
	 * of the base datasets linked datasets one level and not recurse indefinitely.
	 * false if this is the base dataset
	 */
	setupDatasetsById: function(is2ndLevelLinkedDataset = false) {
		this.datasetsById = _.get(this.options, 'datasetsById')
		if (_.isNil(this.datasetsById)) {
			this.datasetsById = {}
			const options = Dataset.getLinkedDatasetSharedModelProperties(this)
			const linkedDatasets = _.map(this.get('linkedDatasets'),
				// #28274 when constructing the dataset, share the datasetsById
				// and combinedFieldsById to go faster and avoid potential bugs
				attributes => wrapModel(attributes, Dataset, options))
			_.extend(this.datasetsById, _.keyBy(linkedDatasets, 'id'))

			let rootDataset = this.get('rootDataset')
			if (rootDataset) {
				if (_.isNil(this.get('rootDatasetId'))) {
					this.set('rootDatasetId', rootDataset.id)
				}
				rootDataset = new Dataset(rootDataset)
				this.datasetsById[rootDataset.id] = rootDataset
				// on the server, these properties aren't passed along to save space
				// so copy them here
				_.each(['filters', 'permissions', 'linkedDatasets'], property => {
					rootDataset.set(property, this.get(property))
				})
			}
			if (this.id) {
				this.datasetsById[this.id] = this
				// #19316 this looks a little strange, but for root datasets it's no
				// different than calling the line above, and for dataset views it just
				// says that any references to the root dataset can go to the dataset
				// view, b/c on the client the dataset view has all the data from the
				// root included
				let rootDatasetId = this.get('rootDatasetId')
				if (_.isNil(rootDatasetId)) {
					this.set('rootDatasetId', this.id)
				}
				rootDatasetId = this.get('rootDatasetId')
				if (_.isNil(this.datasetsById[rootDatasetId])) {
					this.datasetsById[rootDatasetId] = this
				}
				this.on('sync', () => datasetStatus().then(status => status.resetDataset(this.get('rootDatasetId'))))
			}

			if (is2ndLevelLinkedDataset) return

			_.forEach(_.values(this.datasetsById), dataset => {
				_.extend(dataset.datasetsById, this.datasetsById)
				_.extend(dataset.combinedFields, this.combinedFields)
				_.extend(dataset.combinedFieldsById, this.combinedFieldsById)
			})
		}
	},

	setupBuiltInByPrincipalId: function() {
		/**
		 * A mapping of principal id to built-in rollup filter attributes that
		 * we need easy access to.
		 * @type {Dictionary}
		 */
		this.builtInByPrincipalId = _.keyBy(_(this.get('permissions'))
			.filter(p => p.principal?.type === Dataset.ROLLUP_TREE_GROUP)
			.map(({principal, filters}) => ({
				id: principal.id,
				value: Dataset.getBuiltInValueFromFilters(principal, filters)
			})).value(), 'id')
	},

	/**
	 * Adds the "Record is Editable" and "Record Edited" fields to
	 * this.combinedFieldsById
	 */
	addEditFields: function() {
		this.combinedFieldsById[Dataset.RECORD_IS_EDITABLE_ID] =
			getRecordIsEditableField()
		this.combinedFieldsById[Dataset.RECORD_EDITED_ID] =
			getRecordEditedField()
	},

	setFields: function(fields) {
		this.set('fields', fields)
		this.updateCombinedFields()
	},

	updateCombinedFields: function() {
		// combined fields from this dataset and linked datasets
		this.combinedFields = (this.get('fields') || [])
			.filter(field => field.dataType != 'linked-dataset')
		let linkedFields = _(this.get('linkedDatasets'))
			.map('fields')
			.flatten()
			.value()
		this.combinedFields.push(...linkedFields)
		this.combinedFieldsById = _.keyBy(this.combinedFields, 'id')
		this.addEditFields()
	},

	updateVisibleLinkedFieldsById: function() {
		this.visibleLinkedFieldsById = _.keyBy(this.get('visibleLinkedFields'), 'id')
	},

	/**
	 * Returns the field label based on a fieldId and visibleLinkedFieldId
	 * @param vlfLabel true if this field is a VLF, get the VLF's label instead of the
	 * original field's label
	 */
	getFieldLabelByIds: function({ fieldId, visibleLinkedFieldId }) {
		let fieldIdentifier = fieldId
		if (visibleLinkedFieldId && visibleLinkedFieldId > 0) {
			fieldIdentifier = -1 * visibleLinkedFieldId
		}
		return this.getFieldLabel(fieldIdentifier)
	},

	/**
	 * Returns the label for a field
	 * @param vlfLabel true if this field is a VLF, get the VLF's label instead of the
	 * original field's label
	 */
	getFieldLabel: function(fieldOrFieldIdentifier) {
		let fieldIdentifier = _.isObject(fieldOrFieldIdentifier) ? fieldOrFieldIdentifier.id :
			fieldOrFieldIdentifier

		// #45811 check for VLF first since it should have a negative id,
		// which won't be in combinedFields
		let visibleLinkedField = this.getVisibleLinkedFieldByFieldIdentifier(fieldIdentifier)
		if (visibleLinkedField) {
			return visibleLinkedField.label
		}

		let field = this.combinedFieldsById[fieldIdentifier]
		// handle pseudo fields like "Number of _____"
		if (!field && _.isObject(fieldOrFieldIdentifier)) {
			return fieldOrFieldIdentifier.label
		}

		return _.get(field, 'label')
	},

	/**
	 * Converts a field identifier to a fieldId and visibleLinkedFieldId. A
	 * field identifier is used to allow us to mix field IDs and
	 * visibleLinkedFieldIds in a single select. We mix them by multiplying the
	 * visibleLinkedFieldId by -1.
	 *
	 * @typedef {Object} Ids
	 * @property {Number} fieldId
	 * @property {Number} [visibleLinkedFieldId]
	 *
	 * @returns {Ids|null} the IDs, or null if the passed in identifier was null
	 */
	fieldIdentifierToIds: function(fieldIdentifier) {
		if (_.isNil(fieldIdentifier) || fieldIdentifier === '') return null

		if (Dataset.isPseudoOption(fieldIdentifier)) {
			return { fieldId: fieldIdentifier, visibleLinkedFieldId: null }
		}

		let field = this.getFieldByFieldIdentifier(fieldIdentifier)
		if (!field) return null // #30596

		// #30436 must be sure to clear out the `visibleLinkedFieldId`
		let ids = { fieldId: field.id, visibleLinkedFieldId: null }
		if (Dataset.isVisibleLinkedFieldIdentifier(fieldIdentifier)) {
			let vlf = this.getVisibleLinkedFieldByFieldIdentifier(
				fieldIdentifier)
			ids.visibleLinkedFieldId = vlf?.id
		}
		return ids
	},

	/**
	 * Converts IDs to a field identifier. A field identifier is used to allow
	 * us to mix field IDs and visibleLinkedFieldIds in a single select. We mix
	 * them by multiplying the visibleLinkedFieldId by -1.
	 *
	 * @param {Ids} ids
	 */
	idsToFieldIdentifier: function(ids) {
		if (_.isNil(ids)) {
			return null
		}

		if (ids.visibleLinkedFieldId &&
			// #50860 ignore the VLF ID if it's negative, which indicates it
			// was a fake VLF ID generated by MissingVlfCreator
			ids.visibleLinkedFieldId > 0) {
			return -1 * ids.visibleLinkedFieldId
		} else {
			return ids.fieldId
		}
	},

	getDatasetByVisualLinkedFieldId: function(visualLinkedFieldId) {
		let vlf = this.visibleLinkedFieldsById[visualLinkedFieldId]
		return this.getDatasetByFieldIdentifier(-1 * vlf.id)
	},

	/**
	 * Returns the dataset for the given fieldId
	 */
	getDatasetByFieldIdentifier: function(fieldIdentifier) {
		if (_.isNil(fieldIdentifier) || fieldIdentifier == '') return null

		let fieldId = fieldIdentifier
		if (Dataset.isVisibleLinkedFieldIdentifier(fieldIdentifier)) {
			let vlf = this.visibleLinkedFieldsById[-1 * fieldId]
			fieldId = vlf.fieldId
			if (Dataset.isTotal(fieldId)) {
				let datasetId = datasetPaths.lastDatasetId(vlf.path)
				return this.datasetsById[datasetId]
			}
		}
		if (fieldIdentifier == Dataset.RECORD_IS_EDITABLE_ID ||
			fieldIdentifier == Dataset.RECORD_EDITED_ID) {
			return this
		}
		let datasetId = this.combinedFieldsById[fieldId].datasetId
		return this.datasetsById[datasetId]
	},

	/**
	 * Returns the field for the given fieldId. If the ID is for a
	 * visibleLinkedField, we'll decode the ID and return a raw field record.
	 * If the field is for the TOTAL, we'll construct a fake field object with
	 * proper id, label and dataType properties.
	 * @param fieldIdentifier the ID of the field (may be 0 to indicate TOTAL or
	 * negative to indicate a visible linked field ID)
	 * @param [datasetId] if this is a pseudo field, indicates for which
	 * dataset we want to see the pseudo field
	 */
	getFieldByFieldIdentifier: function(fieldIdentifier, datasetId) {
		let pseudoField = this.getPseudoField(fieldIdentifier, datasetId)
		if (pseudoField) return pseudoField

		let fieldId = fieldIdentifier
		if (Dataset.isVisibleLinkedFieldIdentifier(fieldIdentifier)) {
			const vlf = this.getVisibleLinkedFieldByFieldIdentifier(fieldIdentifier)
			const linkedDatasetId = datasetPaths.lastDatasetId(vlf.path)
			if (Dataset.isTotal(vlf.fieldId)) {
				return this.getFieldByFieldIdentifier(Dataset.TOTAL, linkedDatasetId)
			} else {
				fieldId = vlf.fieldId
			}
		}
		return this.combinedFieldsById[fieldId]
	},

	/**
	 * Returns one of the pseudo fields, like "Number of X" or
	 * "Record is Editable", if the corresponding fieldIdentifier is passed in
	 * @param fieldIdentifier the field ID or VLF ID
	 * @param [datasetId] the dataset ID
	 */
	getPseudoField: function(fieldIdentifier, datasetId) {
		if (Dataset.isTotal(fieldIdentifier)) {
			return this.getTotalField(datasetId)
		}
		if (fieldIdentifier == Dataset.RECORD_IS_EDITABLE_ID) {
			return getRecordIsEditableField()
		}
		if (fieldIdentifier == Dataset.RECORD_EDITED_ID) {
			return getRecordEditedField()
		}
	},

	/**
	 * Returns the "Number of X" pseudo field
	 */
	getTotalField: function(datasetId) {
		if (_.isNil(datasetId)) {
			datasetId = this.get('rootDatasetId')
		}
		let dataset = this.datasetsById[datasetId]
		let label
		if (datasetId == this.id) {
			label = i18n.get('datasets.total.tracked', dataset.get('tracked'))
		} else {
			label = fieldAggregationDescription({
				dataset,
				tracked: dataset.get('tracked')
			})
		}
		return {
			id: Dataset.TOTAL,
			dataType: NUMBER,
			datasetId: parseInt(datasetId),
			label
		}
	},

	/**
	 * Returns the field for the given
	 * @param {Ids} ids
	 */
	getFieldByIds: function(ids) {
		let fieldIdentifier = this.idsToFieldIdentifier(ids)
		return this.getFieldByFieldIdentifier(fieldIdentifier)
	},

	/**
	 * Returns the field for the given
	 * @param {string|number} fieldId
	 */
	getField: function(fieldId) {
		fieldId = parseInt(fieldId, 10)
		if (_.isNaN(fieldId)) return

		return this.combinedFieldsById[fieldId]
	},

	getVisibleLinkedFieldByFieldIdentifier: function(fieldIdentifier) {
		if (Dataset.isVisibleLinkedFieldIdentifier(fieldIdentifier)) {
			const vlfId = fieldIdentifier * -1
			let vlf = this.visibleLinkedFieldsById[vlfId]
			if (_.isNil(vlf)) {
				vlf = this.getAllVisibleLinkedFieldsById()[vlfId]
			}
			return vlf
		} else {
			return null
		}
	},

	getDatasetIdByVisibleLinkedFieldId: function() {
		const datasetIdByVisibleLinkedFieldId = {}
		_.forEach(_.values(this.datasetsById), dataset => {
			_.forEach(_.values(dataset.visibleLinkedFieldsById), vlf =>
				datasetIdByVisibleLinkedFieldId[vlf.id] = dataset.id)
		})
		return datasetIdByVisibleLinkedFieldId
	},

	getAllVisibleLinkedFieldsById: function() {
		const allVlfs = _.flatMap(_.values(this.datasetsById), dataset =>
			_.values(dataset.visibleLinkedFieldsById))
		return _.keyBy(allVlfs, 'id')
	},

	/**
	 * For a given field or field ID, returns the corresponding dataset as a
	 * plain JSON object (as opposed to as a Dataset model)
	 * @return {Object} the dataset
	 */
	getDatasetForField: function(field) {
		if (_.isNil(field)) {
			return field
		}

		let fieldIdentifier = _.get(field, 'id', field)
		if (DATASET_EDIT_FIELDS.includes(fieldIdentifier)) {
			return this.datasetsById[this.id]
		}

		if (field.datasetId) {
			return this.datasetsById[field.datasetId]
		}

		if (Dataset.isVisibleLinkedFieldIdentifier(fieldIdentifier)) {
			let vlf = this.visibleLinkedFieldsById[-1 * fieldIdentifier]
			let datasetId = datasetPaths.lastDatasetId(vlf.path)
			return this.datasetsById[datasetId]
		}

		return _.find(_.values(this.datasetsById), dataset => {
			if (dataset.fields.get(field)) {
				return true
			}
		})
	},

	getVisibleLinkedFieldOption: function(visibleLinkedFieldId) {
		let vlf = this.visibleLinkedFieldsById[visibleLinkedFieldId]
		let field = this.combinedFieldsById[vlf.fieldId]
		return _.extend({}, field, {
			id: -1 * vlf.id,
			fieldId: vlf.fieldId,
			label: vlf.label
		})
	},

	/**
	 * @returns {Array} the fields as an array of plain JavaScript objects
	 */
	getNumericFields: function() {
		return _.filter(this.get('fields'), function(field) {
			return isNumericDataType(field.dataType)
		})
	},

	/**
	 * @returns {Boolean} true if this is a private dataset
	 */
	isPrivate: function() {
		return _.size(this.get('permissions')) === 1 &&
			this.get('permissions')[0].principal.type === 'user'
	},

	getTypeDescription: function() {
		let key = 'datasets.dataset'
		let type = this.get('type')
		if (type === Dataset.FOLDER) {
			key = 'bookmarks.folder'
		} else if (type === Dataset.VIEW) {
			key = 'datasets.view'
		}
		return i18n.get(key)
	},

	/**
	 * @return {Array} an array of String field groups
	 */
	getFieldGroups: function(fields) {
		fields = _.defaultTo(fields, this.get('fields'))
		return _(fields)
			.map('group')
			.filter(group => !_.isEmpty(group))
			.uniq()
			.orderBy(group => group.toLocaleUpperCase())
			.value()
	},

	getFieldsForGroup: function(group) {
		return _.filter(this.get('fields'), field => field.group === group)
	},

	getCalendarFieldIdentifier: function() {
		let ids
		if (this.get('selectedCalendarFieldId')) {
			ids = {
				fieldId: this.get('selectedCalendarFieldId'),
				visibleLinkedFieldId: this.get('selectedCalendarVisibleLinkedFieldId')
			}
		} else {
			ids = {
				fieldId: this.get('defaultCalendarFieldId'),
				visibleLinkedFieldId: this.get('defaultCalendarVisibleLinkedFieldId')
			}
		}
		return this.idsToFieldIdentifier(ids)
	},

	/**
	 * Returns a description of the aggregation using the provided fields, such
	 * as "Average Amount by Date"
	 */
	fieldAggregationDescription: function (opt) {
		let aggregationField = this.combinedFieldsById[opt.aggregationFieldId]
		let aggregationVisibleLinkedField = this.visibleLinkedFieldsById[opt.aggregationVisibleLinkedFieldId]
		let aggregationType = opt.aggregationType
		let calendarField = this.combinedFieldsById[opt.calendarFieldId]

		return fieldAggregationDescription({
			dataset: this,
			aggregationField,
			aggregationVisibleLinkedField,
			aggregationType,
			calendarField,
			tracked: this.get('tracked')
		})
	},

	cloneDeep: function() {
		let clone = Backbone.Model.prototype.cloneDeep.apply(this, arguments)
		// this isn't in the generic cloneDeep functionality added by
		// setup-backbone.js
		clone.initialize()
		return clone
	},

	/**
	 * Returns a field.  If the field is linked, returns the field the linked
	 * field points to.  Accepts a field ID or a Field object
	 * @param {Object|String|Number} field the field ID or pre-populated field
	 * object
	 * @returns {Object} the field as a plain JavaScript object
	 */
	getUnderlyingField: function(field) {
		// first, see if the "field" was a fieldId
		if (_.isNumber(field) || _.isString(field)) {
			field = this.combinedFieldsById[field]
		}
		let pseudoField = this.getPseudoField(field, this.id)
		if (pseudoField) return pseudoField

		// unwrap any Backbone models
		field = unwrapModel(field)
		if (_.get(field, 'dataType') === DATASET_LINK) {
			let pkFieldId
			if (field.linkedDatasetId == this.get('rootDatasetId')) {
				pkFieldId = this.get('primaryKeyFieldId')
			} else {
				const linkedDataset = this.datasetsById[field.linkedDatasetId]
				if (_.isNil(linkedDataset)) return field

				pkFieldId = linkedDataset.get('primaryKeyFieldId')
			}
			if (_.isNil(pkFieldId)) return field

			return unwrapModel(this.combinedFieldsById[pkFieldId])
		}
		return field
	},

	hasPrimaryKeyField: function() {
		let primaryKeyFieldId = this.get('primaryKeyFieldId')
		return !_.isNil(primaryKeyFieldId) && primaryKeyFieldId != ''
	},

	hasRecordId: function() {
		const metadata = this.get('dataSourceMetadata')
		return !_.isNil(metadata?.primaryKey) &&
			!_.isEmpty(metadata?.primaryKey) // in case the PK is an empty string
	},

	/**
	 * @return {boolean} true if the dataset and all linked datasets are in a ready state
	 */
	isReady: function() {
		return this.isComplete() && this.isLinkedDatasetsReady()
	},

	/**
	 * @return {boolean} true if all linked datasets are in a ready state or
	 * if there arenot any linked datasets
	 */
	isLinkedDatasetsReady: function() {
		let linkedDatasets = this.attributes.linkedDatasets || []
		return _.isEmpty(linkedDatasets) || _.every(linkedDatasets, c => this.isComplete(c.status))
	},

	/**
	 * @return {boolean} true if the dataset is currently building
	 */
	isBuilding: function() {
		const testHasFinishedStatus = dataset => _.includes([
			Dataset.READY, Dataset.INDEXED, Dataset.ERROR
		], unwrapModel(dataset).status)

		const selfIsFinished = testHasFinishedStatus(this)

		const allLinkedAreFinished = _.every(this.attributes.linkedDatasets, it => {
			const hasFinishedStatus = testHasFinishedStatus(it)
			const hasFinishedProgress = it.progress >= 1
			const isErrored = it.status === Dataset.ERROR

			const statusAndProgressIndicateCompletion =
				hasFinishedStatus && hasFinishedProgress
			const stoppedWithError = isErrored && !hasFinishedProgress

			return statusAndProgressIndicateCompletion || stoppedWithError
		})

		return !selfIsFinished || !allLinkedAreFinished
	},

	/**
	 * Matches the logic in {@see com.spider.cms.datasets.dao.DatasetsQueryDao#query(DatasetsQueryRequest)}
	 * @return {boolean} true if the dataset can be queried and displayed
	 */
	isDisplayable: function() {
		const type = this.get('type')
		const canQuery = (type == Dataset.DATASET && this.builtAtLeastOnce()) || type == Dataset.VIEW
		const hasFields = !_.isEmpty(this.get('fields'))
		return this.get('status') != Dataset.ERROR && canQuery && hasFields
	},

	isEditable: function() {
		const {datasetOrDescendant, type} = this.attributes
		let isEditable = false
		if (datasetOrDescendant) {
			if (type === 'dataset') {
				isEditable = this.isModifyDataset() || this.isModifyPermission()
			} else {
				isEditable = this.isModifyView()
			}
		} else { // Folder not under a dataset, user must be able to modify datasets
			isEditable = this.isModifyDataset()
		}
		return isEditable
	},

	hasStandardPermissions: function() {
		// #37527 Must match on the datasetId of whatever is currently loaded
		// (e.g. view/folder)
		const permissionsForThis = _.filter(this.get('permissions'),
			p => p.datasetId == this.get('rootDatasetId'))
		return _.isEmpty(permissionsForThis)
	},

	/**
	 * @returns {Boolean} true if the current user can modify the permissions
	 */
	isModifyPermission: function() {
		const {organizationId, modifyPermissions} = this.attributes
		// Note: there is no org level permission for modifying permissions
		return permissions.modifyAllDatasets(organizationId) || modifyPermissions
	},

	/**
	 * @returns true if the user can modify views for this dataset
	 */
	isModifyView: function() {
		return this._hasPermission('modifyViews', 'modifyDatasetViews')
	},

	/**
	 * @returns true if the user can modify records for this dataset
	 */
	isModifyRecords: function() {
		return this._hasPermission('modifyRecords', 'modifyDatasetRecords')
	},

	/**
	 * @returns true if the user can view records for this dataset
	 */
	isViewRecords: function() {
		return this._hasPermission('viewRecords', 'viewDatasetRecords')
	},

	/**
	 * @returns true if the user can view records tab for this dataset
	 */
	isViewRecordsTab: function() {
		return this._hasPermission('viewRecordsTab', 'viewDatasetRecordsTab')
	},

	/**
	 * @returns true if the user can modify this dataset
	 */
	isModifyDataset: function() {
		const {organizationId} = this.attributes
		return permissions.modifyAllDatasets(organizationId) || // This overrides advanced permissions
			this._hasPermission('administer', 'modifyStandardPermissionDatasets')
	},

	/**
	 * See if the user has permission to do the provide dataset operation.
	 * Checks the dataset level attribute first, then any org level permission
	 * for standard datasets.  If the dataset has advanced permission we must
	 * not consider the org level permission. (#35130, #35129)
	 *
	 * @param datasetAttribute This attribute will be checked first
	 * @param orgLevelPermissionFn the `permissions` function to invoke with the orgId
	 */
	_hasPermission: function(datasetAttribute, orgLevelPermissionFn) {
		const {organizationId, administer} = this.attributes
		const datasetPermission = this.get(datasetAttribute)
		return administer || datasetPermission || // Advanced Permission
			(this.hasStandardPermissions() && _.invoke(permissions,
				orgLevelPermissionFn, organizationId))
	},

	/**
	 * @param {Dataset} options.relative the dataset, folder or view _relative_
	 * to which this view or folder will be added
	 *
	 * @returns {boolean} true if the user is not able to modify the dataset/folder
	 */
	shouldHidePermissions: function(options) {
		const {datasetOrDescendant} = this.attributes
		let hidePermission = !this.isModifyPermission()

		// hide the permissions panel for folders which aren't under datasets
		if (this.get('type') === 'folder') {
			// if we're creating relative to a dataset, the folder will be a
			// sibling so it will be top-level and the permissions panel should
			// be hidden
			if (this.isNew()) {
				if (options.relative.get('type') === 'dataset') {
					hidePermission = true
				} else if (!options.relative.get('datasetOrDescendant')) {
					hidePermission = true
				}
			} else if (!datasetOrDescendant) {
				hidePermission = true
			}
		}

		return hidePermission
	},

	/**
	 * Given a filter (and optionally a principal if it's new) determine if it
	 * is a built-in rollup filter for a tree template permission.
	 *
	 * @param {Filter} filter
	 * @param {Principal} [principal] If no provided it will be looked up from
	 *     filter.permissionId
	 */
	isFilterBuiltInRollupTemplateFilter(filter = {}, principal) {
		if (_.isNil(principal)) {
			principal = this.getPrincipalForFilter(filter)
		}
		if (_.isNil(principal)) {
			// We're assuming this is a new permission; which is safe for now
			return Dataset.isBuiltInFilter(filter)
		}
		return principal.type === Dataset.ROLLUP_TREE_GROUP && Dataset.isBuiltInFilter(filter)
	},

	getPrincipalForFilter(filter = {}) {
		const {permissionId} = filter
		return _.get(this.permissionsById[permissionId], 'principal')
	},

	/**
	 * Given a principal (from a principal panel row) return the value that
	 * should be pre-selected in the rollup dropdown.  This reads from the
	 * model, for setting the initial state.  Once the user makes changes the
	 * state is associated to the TR data() and this must not be used.
	 * @param principal
	 */
	getBuiltInValueForTemplatePrincipal(principal = {}) {
		// The caller knows this is a template principal, but let's play it safe
		if (principal.type === Dataset.ROLLUP_TREE_GROUP) {
			return _.get(this.builtInByPrincipalId[principal.id], 'value')
		}
	},

	/**
	 * assign the selected isAny filter value to principal.rollupPermission
	 * (suitable for making the selection in the `rollupFilterDropdown`)
	 *
	 * @param principal A principal object
	 * @parma filter Must be the built-in filter for the template principal
	 */
	assignBuiltInIsAnyValue: function(principal, filter) {
		const {type, treeRootId} = principal || {}
		if (type === Dataset.ROLLUP_TREE_GROUP) {
			if (filter && filter.rollupIsAnyFilters) {
				principal.rollupPermission = filter.rollupIsAnyFilters[treeRootId]
			}
		}
	},

	/**
	 * Matches {@see com.spider.cms.datasets.model.Dataset#builtAtLeastOnce}
	 * @returns {boolean} whether or not the dataset has been built at least once
	 */
	builtAtLeastOnce: function() {
		return !_.isNil(this.get('numTracked'))
	},

	/**
	 * Matches {@see com.spider.cms.datasets.model.Dataset#isComplete}
	 * @param [status] a valid dataset status to evaluate for completion; if null,
	 * will use this.get('status')
	 * @return {boolean} true if the current dataset is in a completed state
	 */
	isComplete: function(status) {
		status = status || this.get('status')
		return status == Dataset.READY || status == Dataset.INDEXED
	},

	/**
	 * Gets the dataset attributes (status, progress, label) from the
	 * lowest progress value from the dataset and all of it's linked datasets
	 * taking into account any updates received via a websocket push
	 *
	 * @return {Promise<{status, progress, label}>} fulfilled once websockets are setup and a minimum is determined
	 */
	getLowestProgress: function() {
		return datasetStatus().then(status => {
			const datasetProgressList = _.map(this.datasetsById, (dataset) => this.getProgressFromStatus(status, dataset))
			return _.minBy(datasetProgressList, 'progress')
		})
	},

	/**
	 * Gets the dataset attributes (status, progress, label)
	 * @param {Object} rawStatus the status object returned from {@link datasetStatus().then}
	 * @returns {{progress: *, id, label: boolean, status: *}}
	 */
	getProgressFromStatus: function(rawStatus, dataset)  {
		const { rootDatasetId, label, status, progress} = dataset.attributes
		const currentProgress = _.get(rawStatus.get(rootDatasetId), 'progress', progress)
		const currentStatus = _.get(rawStatus.get(rootDatasetId), 'status', status)
		// No label if it's the currently selected dataset
		const linkedLabel = rootDatasetId != this.get('rootDatasetId') && label
		return {id: rootDatasetId, progress: currentProgress, status: currentStatus, label: linkedLabel }
	},

	// NOTE: same logic in ScorecardsDao.get
	/**
	 * @param {Ids} ids
	 */
	seriesEquationLabel: function(ids) {
		return this.get('label') + ' > ' + (Dataset.isTotal(ids.fieldId) ?
			this.get('tracked') : this.getFieldLabelByIds(ids))
	},

	isSelectedCalendarFieldDefined: function() {
		let selectedCalendarFieldId = this.get('selectedCalendarFieldId')
		return !_.isNil(selectedCalendarFieldId) &&
			selectedCalendarFieldId != Dataset.SNAPSHOT
	},

	toJSON() {
		//unpersisted objects need null ids so that they can be persisted in
		//the backend but on the frontend objects must all of id's defined so
		//that UI logic can work on both persisted and unpersisted states
		_.each(this.attributes.visibleLinkedFields, vlf => {
			vlf.id = _.startsWith(vlf.id, UNSAVED_PREFIX) ? null : vlf.id
		})
		return Backbone.Model.prototype.toJSON.apply(this, arguments)
	},

	headerRowSelected: function() {
		return true
	},

	headerRowIndex: function() {
		const headerRow = this.get('dataSourceMetadata').headerRow
		return headerRow ? headerRow : 0
	},

	isManuallyAddedFieldId: function(id) {
		const manuallyAddedFieldIds = []
		const addFieldIds = d => {
			if (d.manuallyAddedFieldId) {
				manuallyAddedFieldIds.push(d.manuallyAddedFieldId)
			}
		}
		addFieldIds(this.attributes)
		_.forEach(this.get('linkedDatasets'), addFieldIds)

		return manuallyAddedFieldIds.includes(id)
	},

	getManuallyAddedField: function() {
		const manuallyAddedFieldId = this.get('manuallyAddedFieldId')
		if (!manuallyAddedFieldId) {
			return
		}

		return _.find(this.get('fields'), field => field.id === manuallyAddedFieldId)
	},

	getManuallyAddedFieldId: function() {
		return this.get('manuallyAddedFieldId')
	},

	/**
	 * Returns whether the path to the given dataset is ambiguous (i.e. there's
	 * more than one possible path). In that case we would need to display a
	 * "Path to Dataset" button to disambiguate the path
	 * @param {String|Number} the ID of the dataset
	 */
	isAmbiguousPath: function(datasetId) {
		const onlyUniquePaths = this.get('onlyUniquePaths')
		if (_.isNil(onlyUniquePaths)) return false

		datasetId = parseInt(datasetId)
		let onlyUniquePath = _.includes(onlyUniquePaths,
			datasetId)
		return !onlyUniquePath && datasetId != this.get('rootDatasetId')
	},

	/**
	 * Returns the label for a cluster with a particular index
	 * @param {DatasetField|Number|Object} field the field model, field ID or
	 * a plain JavaScript object with the field info
	 * @param clusterIndex the index of the cluster (0 based)
	 * @returns {String}
	 */
	getClusterLabel: function(field, clusterIndex) {
		clusterIndex = parseInt(clusterIndex)
		if (_.isNaN(clusterIndex) || _.isNil(field)) {
			return null
		}
		const unwrappedField = unwrapModel(field)
		const fieldId = typeof unwrappedField === NUMBER
			? unwrappedField :unwrappedField.id
		if (!_.isNil(fieldId)) {
			field =	this.combinedFieldsById[fieldId]
		}
		if (!field.clusterLabelsByIndex) {
			field.clusterLabelsByIndex =
				_.keyBy(field.clusterLabels, 'index')
		}
		let clusterInfo = field.clusterLabelsByIndex[clusterIndex]
		if (clusterInfo) {
			return _.get(clusterInfo, 'label', clusterIndex)
		} else {
			return i18n.get('datasets.field.clusterFields.label.default.withArg',
				clusterIndex+1)
		}
	},

	getDisplayableFields: function() {
		const allFields = this.get('fields')
		return _.filter(allFields, field => {
			const isNewField = !_.isNil(field.unsavedFieldId) && _.isNil(field.id)
			const isManuallyAddedField = !_.isNil(field.id) &&
				this.isManuallyAddedFieldId(field.id)
			return isNewField || !isManuallyAddedField
		})
	},

	/**
	 * see https://github.com/SpiderStrategies/Scoreboard/issues/46725#issuecomment-1092161129
	 */
	isQueryable: function() {
		return _.includes(QUERYABLE_STATUSES, this.get('status'))
	},

	/**
	 * Returns whether the given dataset is on the "one" side of a one-to-many
	 * relationship with this dataset (either directly or transitively)
	 * @param datasetId the ID of the dataset whose relationship is to be
	 * compared with this dataset
	 * @returns {Boolean}
	 */
	isOneSide: function(datasetId) {
		datasetId = parseInt(datasetId)
		if (this.id == datasetId ||
			this.get('rootDatasetId') == datasetId) {
			return true
		}
		return _.includes(this.get('oneDatasetIds'), datasetId)
	},

	/**
	 * Analogous to Dataset.java#getBasicFieldsByIntermediateTableColumn
	 */
	getBasicFieldsByIntermediateTableColumn: function() {
		const basicFields = _.filter(this.get('fields'), field =>
			field?.fieldType === DATASET_FIELD_TYPE.BASIC)
		return _.keyBy(_.values(basicFields), field =>
			getIntermediateTableColumnFromBasicEquation(field?.equation))
	}

}, /** @lends Dataset */ {

	// value for the dataset type indicating a root dataset
	DATASET: 'dataset',
	// value for a dataset type indicating a dataset view
	VIEW: 'view',
	// value for a dataset type indicating a dataset folder
	FOLDER: 'folder',

	// DatasetStatus, but for the client
	READY: 'ready',
	STALE: 'stale',
	LOADING: 'loading',
	INDEXED: 'indexed',
	ERROR: 'error',
	STALE_METADATA: 'stale-metadata',
	AWAITING_RESPONSE: 'awaitingResponse',

	// in a datasets query, indicates a blank value
	BLANK: '~[blank]~',

	// fake ID representing Total Records (e.g. Total Contracts).  Used when
	// choosing aggregations (aka "Choose Columns" and setting the total display
	// in the upper right corner.  Can't use -1 because that would preclude a
	// dataset ID of 1 being used in linking relationships because of how we
	// use -1 * datasetId in dropdowns that need to mix field IDs with
	// "Total (tracked)" from a linked dataset
	TOTAL: 0,
	// fake calendar ID indicating we should show results for All Time
	ALL_TIME: -1,
	// fake calendar ID indicating the "Snapshot of Current Data" option should
	// be used (#19791)
	SNAPSHOT: -2,
	// fake id representing the 'Has any permissions filter' option for
	// dataset permissions
	ANY_FILTER: -3,
	/** Fake field ID for the "Record is Editable" filter (#54607) */
	RECORD_IS_EDITABLE_ID: -10,
	/** Fake field ID for the "Record Edited" filter (#54607) */
	RECORD_EDITED_ID: -11,
	/** The names of the available dataset permissions */
	PERMISSION_KEYS: ['administer', 'modifyPermissions', 'modifyViews',
		'modifyRecords', 'viewRecords'],

	/**
	 * The domain type for rollup tree groups, this is set as the principal
	 * type for tree templates listed in the principal panels for dataset
	 * permissions
	 */
	ROLLUP_TREE_GROUP: 'rollup-tree-group',

	/**
	 * @type {string} The event name for a dataset drilldown
	 *
	 * Datasets are unique in that when we drill down into them there is state
	 * that we may want to pass along.
	 *
	 * Additionally, if we know we are navigating to a dataset from another
	 * section and/or org we need to ensure the user has permission to view the
	 * dataset and if they don't prevent the navigation.  So for dataset tree
	 * XHRs we pass an 'ensureViewable' parameter so force the server to
	 * validate the user's permission to the dataset.
	 */
	DRILLDOWN: 'datasets:drilldown',

	/**
	 * Returns whether the given fieldId indicates a Total number of records
	 * instead of a specific field.
	 */
	isTotal,

	/**
	 * Returns whether this is one of the pseudo options
	 * (TOTAL, ALL_TIME, SNAPTSHOT, or ANY_FILTER)
	 */
	isPseudoOption: function(fieldId) {
		return fieldId == Dataset.TOTAL || fieldId == Dataset.ALL_TIME ||
			fieldId == Dataset.SNAPSHOT || fieldId == Dataset.ANY_FILTER
	},

	getFieldIdentifier: function(fieldId, visibleLinkedFieldId) {
		return visibleLinkedFieldId ? -1 * visibleLinkedFieldId : fieldId
	},

	/**
	 * Returns whether the given fieldId indicates a visibleLinkedFieldId
	 * instead of a fieldId
	 */
	isVisibleLinkedFieldIdentifier: function(fieldIdentifier) {
		// VLF IDs start from -1000 and become increasingly negative from there
		// ... so -999 can't be a VLF ID
		// ... and -1000, -1001, -1002, etc. are valid VLF IDs.
		// see the definition of VisibleLinkedFieldHDao.id, and specifically the
		// INITIAL_PARAM value
		return fieldIdentifier <= -1000
	},

	/**
	 * Returns true if the given value represents a blank value in dataset query
	 * results.
	 */
	isBlank: function(value) {
		// currently it seems for rows, blanks come through as 'blank' but
		// for "values" they come through as nil
		return _.isNil(value) || value === Dataset.BLANK
	},

	/**
	 * @param {DatasetFilter} a filter that is known to belong to a rollup
	 *     template principal
	 * @returns {boolean} true if it's the built-in filter
	 * @private
	 */
	isBuiltInFilter: function(filter) {
		return filter.deletable === false && filter.linkGroup == 1
	},

	/**
	 * @param principal A rollup tree template principal
	 * @param filters The filters for a rollup template permission
	 * @returns the rollupIsAnyFilters value for the built-in filter
	 */
	getBuiltInValueFromFilters: function(principal, filters) {
		const filter = _.find(filters, Dataset.isBuiltInFilter)
		if (filter) {
			return filter.rollupIsAnyFilters[principal.treeRootId]
		}
	},

	/**
	 * Returns the correct icon for a dataset node with the given type
	 */
	icon: function(type) {
		return type === 'view' ? 'dataset-view' : type
	},

	// Returns the correct dataset-related widget icon based on the widget error message shown
	widgetErrorIcon: function(message) {
		if (message === 'common.security.missing.datasets.view') {
			return 'locked'
		} else if (message === 'datasets.notReady') {
			return 'dataset'
		} else {
			return 'dataset-has-error'
		}
	},

	getAutomaticFieldsByLabel: function(dataset) {
		dataset = unwrapModel(dataset)
		if (_.isNil(dataset?.dataSourceMetadata)) return {}

		return _.keyBy(dataset?.dataSourceMetadata?.fields, 'label')
	},

	getFieldsByLabel: function(dataset) {
		dataset = unwrapModel(dataset)
		if (_.isNil(dataset?.fields)) return {}

		return _.keyBy(dataset?.fields, 'label')
	},

	/**
	 * Creates Dataset models from the passed in datasets, sharing datasetsById,
	 * combinedFields, and combinedFieldsById from previously created Dataset
	 * models that are linked Datasets just like in {@link dataset.js#setupDatasetsById}
	 * See #28274
	 * @param {Object[]} datasets
	 * @returns {Dataset[]}
	 */
	wrapDatasetModelsSharingProperties: function(datasets) {
		const createdModelsByLinkedDatasetId = {}

		function updateCreatedModelsByLinkedDatasetId(datasetModel) {
			_.forEach(datasetModel.linkedDatasets, linkedDataset => {
				if (!_.isNil(createdModelsByLinkedDatasetId[linkedDataset.id])) return

				createdModelsByLinkedDatasetId[linkedDataset.id] = datasetModel
			})
		}

		return _.map(datasets, dataset => {
			// if this is already a Dataset model, update the createdModelsByLinkedDatasetId
			// and return the model the way it is
			if (dataset instanceof Dataset) {
				updateCreatedModelsByLinkedDatasetId(dataset)
				return dataset
			}

			const existingLinkedDataset = createdModelsByLinkedDatasetId[dataset.id]
			const options = !_.isNil(existingLinkedDataset) ?
				Dataset.getLinkedDatasetSharedModelProperties(
					existingLinkedDataset) : {}

			/**
			 *	@type {Dataset}
			 */
			const datasetModel = wrapModel(dataset, Dataset, options)
			updateCreatedModelsByLinkedDatasetId(datasetModel)

			return datasetModel
		})
	},

	getLinkedDatasetSharedModelProperties: function(existingDataset) {
		return {
			datasetsById: existingDataset.datasetsById,
			combinedFields: existingDataset.combinedFields,
			combinedFieldsById: existingDataset.combinedFieldsById
		}
	}

}, { isTotal })

/**
 * Returns the "Record Edited" pseudo field
 */
function getRecordEditedField() {
	return {
		id: Dataset.RECORD_EDITED_ID,
		label: i18n.get('datasets.field.recordEdited'),
		dataType: YES_NO
	}
}

/**
 * Returns the "Record Is Editable" pseudo field
 */
function getRecordIsEditableField() {
	return {
		id: Dataset.RECORD_IS_EDITABLE_ID,
		label: i18n.get('datasets.field.recordIsEditable'),
		dataType: YES_NO
	}
}

// #46492 STALE should be considered "ready", since query results will be returned
// https://github.com/SpiderStrategies/Scoreboard/issues/46725#issuecomment-1101418108
const QUERYABLE_STATUSES = [Dataset.READY, Dataset.INDEXED, Dataset.STALE, Dataset.LOADING]

const DATASET_EDIT_FIELDS = [Dataset.RECORD_IS_EDITABLE_ID,
		Dataset.RECORD_EDITED_ID]

function isEditField(fieldIdentifier) {
	if (_.isString(fieldIdentifier)) fieldIdentifier = parseInt(fieldIdentifier)
	return DATASET_EDIT_FIELDS.includes(fieldIdentifier)
}

export default Dataset
export { ROLLUPS_NOT_VIEWABLE, getRecordEditedField, getRecordIsEditableField,
	DATASET_EDIT_FIELDS, isEditField }
Dataset.__filename__ = 'datasets/model/dataset.js'
